import {
  PartnerCategory,
  PartnerCategoriesResponse,
  PartnerCategoryResponse,
  PartnerCategoryStatsResponse,
  CreatePartnerCategoryRequest,
  UpdatePartnerCategoryRequest
} from '../types/partnerCategory'

// API URL - using CRO Service
const getApiUrl = (): string => {
  const envUrl = import.meta.env.VITE_CRO_API_URL
  const mode = import.meta.env.MODE

  if (envUrl) {
    return envUrl
  }

  const API_URLS = {
    local: 'http://localhost:3007/api/v1',
    remote: 'https://ath-avi-cro-uat-west.azurewebsites.net/api/v1'
  }

  return mode === 'remote' ? API_URLS.remote : API_URLS.local
}

class PartnerCategoryService {
  private baseURL: string

  constructor() {
    this.baseURL = getApiUrl()
  }

  private getAuthToken(): string | null {
    return localStorage.getItem('auth-token')
  }

  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    const token = this.getAuthToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    return headers
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(
        errorData.error?.message || errorData.message || `HTTP ${response.status}`
      )
    }
    return response.json()
  }

  /**
   * Get all partner categories
   * @param isActive - Optional filter by active status
   */
  async getCategories(isActive?: boolean): Promise<PartnerCategory[]> {
    const url = new URL(`${this.baseURL}/partner-categories`)

    if (isActive !== undefined) {
      url.searchParams.append('is_active', String(isActive))
    }

    const timeoutMs = 30000 // 30 second timeout

    const config: RequestInit = {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    // Create timeout promise
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const data = await this.handleResponse<PartnerCategoriesResponse>(response)

    return data.data
  }

  /**
   * Get a single category by ID
   * @param id - Category UUID
   */
  async getCategory(id: string): Promise<PartnerCategory> {
    const url = `${this.baseURL}/partner-categories/${id}`
    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url, config), timeoutPromise])
    const data = await this.handleResponse<PartnerCategoryResponse>(response)

    return data.data
  }

  /**
   * Create a new partner category
   * @param categoryData - Category data
   */
  async createCategory(categoryData: CreatePartnerCategoryRequest): Promise<PartnerCategory> {
    const url = `${this.baseURL}/partner-categories`
    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'POST',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify(categoryData)
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url, config), timeoutPromise])
    const data = await this.handleResponse<PartnerCategoryResponse>(response)

    return data.data
  }

  /**
   * Update an existing partner category
   * @param id - Category UUID
   * @param updates - Partial category data to update
   */
  async updateCategory(id: string, updates: UpdatePartnerCategoryRequest): Promise<PartnerCategory> {
    const url = `${this.baseURL}/partner-categories/${id}`
    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'PUT',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify(updates)
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url, config), timeoutPromise])
    const data = await this.handleResponse<PartnerCategoryResponse>(response)

    return data.data
  }

  /**
   * Get category statistics (partner count)
   * @param id - Category UUID
   */
  async getCategoryStats(id: string): Promise<{ category: PartnerCategory; stats: { total_partners: number } }> {
    const url = `${this.baseURL}/partner-categories/${id}/stats`
    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url, config), timeoutPromise])
    const data = await this.handleResponse<PartnerCategoryStatsResponse>(response)

    return data.data
  }
}

export const partnerCategoryService = new PartnerCategoryService()
